import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { MainLayout } from 'views/layouts'
import { useNotification } from 'lib/context/notification.context'
import { fetchHomeUserEvaluated, fetchUserConsented } from '../../services/fetch-home-user-evaluated'
import { GetDataHome, cleanPreviousAnswers, setBetesaResultId, setCurrentQuestion } from '../../store'
import {
  setProcessSelected,
  setHaveSkillFinished,
  setHaveBetesaInterpreted,
  setUserTestId,
  userTestIdSelector,
  setIsBetesaTest,
  dataHomeGeneralSelector,
} from 'ducks/evaluatedUser'
import { logout } from 'ducks/user'
import { logout as logoutAuth } from 'ducks/auth'

import {
  CardTest,
  CardTestItem,
  CardTestItemContentDescription,
  CardTestItemContentDiv,
  CardTestItemContentDurationDiv,
  CardTestItemContentParagraph,
  CardTestItemContentStartDiv,
  CardTestItemContentTitle,
  CardTestItemImgDiv,
  ContainerCards,
  ContainerTest,
  TestItemImg,
  TitleCard,
  NotTest,
  ButtonContainer,
  ProcessHint,
} from './styled'
import skillsImg from 'assets/images/icon-skills-card.png'
import talentsImg from 'assets/images/icon-betesa-card.png'
import test360Img from 'assets/images/icon-test360-card.png'
import ClockIcon from 'assets/icons/ClockIcon'
import FileFolrderIcon from 'assets/icons/fileFolderIcon'
import { StrokedButton } from 'views/components/UI/Buttons'
import { ProcessSelected } from 'types/common'
import { setTestId } from 'views/screens/super-admin/percentiles/store'
import { setDataHomeGeneral } from 'ducks/evaluatedUser/selectors/dataHome'
import { useTranslation } from 'react-i18next'
import { setHas360Test } from 'ducks/test360'
import ModalLoader from 'views/components/UI/ModalLoader'

interface StartTest {
  companyName: string
  testLanguage?: string
  testType: string
  companyId: number
  processId?: number
  questionnaireId?: number
  processQuestionnaireId?: number
  questionnaireName?: string
}

function Home() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { getError, getWarning } = useNotification()
  const [itemsThatMatch, setItemsThatMatch] = useState<GetDataHome[]>([])
  const [loading, setLoading] = useState(false)
  const userTestIdStored = useSelector(userTestIdSelector)
  const dataHome = useSelector(dataHomeGeneralSelector)
  const [currentBetesaResultId, setCurrentBetesaResultId] = useState(null)
  const { t } = useTranslation()

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const response = await fetchHomeUserEvaluated()
        if (response.length === 0) {
          getError('No hay procesos disponibles')
          dispatch(logout())
          dispatch(logoutAuth())
        }

        response.filter((item: any) => {
          const typeCategoryFinished = item.type_category.filter(
            (typeFinishInfo: any) => typeFinishInfo.isFinished || typeFinishInfo.isInterpreted
          )

          if (typeCategoryFinished.length === 1) {
            if (typeCategoryFinished[0].isFinished) {
              dispatch(setHaveSkillFinished(true))
            } else {
              dispatch(setHaveBetesaInterpreted(true))
            }
          }

          if (typeCategoryFinished.length === 2) {
            dispatch(setHaveSkillFinished(true))
            dispatch(setHaveBetesaInterpreted(true))
          }

          return true
        })

        dispatch(setDataHomeGeneral(response))
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [dispatch, getError])

  const handleSetProcessSelected = ({
    companyName,
    testLanguage,
    testType,
    companyId,
    userTestId,
    processId,
    questionnaireId,
    processQuestionnaireId,
    questionnaireName,
  }: ProcessSelected) => {
    dispatch(
      setProcessSelected({
        companyName,
        testLanguage,
        testType,
        companyId,
        userTestId,
        processId,
        questionnaireId,
        processQuestionnaireId,
        questionnaireName,
      })
    )
  }

  const handleStartTest = (
    {
      companyName,
      testLanguage,
      testType,
      companyId,
      processId,
      questionnaireId,
      processQuestionnaireId,
      questionnaireName,
    }: StartTest,
    testId: number,
    userTestId: number,
    answerDevice?: string
  ) => {
    const userAgent = navigator.userAgent
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)
    const deviceType: 'DESKTOP' | 'MOBILE' = isMobile ? 'MOBILE' : 'DESKTOP'

    if (isMobile && testType === 'Prueba360') {
      return getWarning(t('evaluatedUser.notifications.noMobileTest360'))
    }

    if (answerDevice !== deviceType && answerDevice) {
      return getWarning(
        t(
          answerDevice === 'DESKTOP'
            ? 'evaluatedUser.notifications.noSwitchDevicePC'
            : 'evaluatedUser.notifications.noSwitchDeviceMobile'
        )
      )
    }

    handleSetProcessSelected({
      companyName: companyName,
      testLanguage: testLanguage ?? 'ES',
      testType: testType,
      companyId,
      userTestId,
      processId,
      questionnaireId,
      processQuestionnaireId,
      questionnaireName,
    })

    if (testType === 'Cuestionario' && questionnaireId && processQuestionnaireId) {
      navigate('/test/questionnaire', {
        state: {
          questionnaireId,
          processQuestionnaireId,
          questionnaireName,
          processName: companyName,
          processId,
        },
      })
      return
    }

    if (userTestIdStored !== userTestId) {
      dispatch(setCurrentQuestion(0))
      dispatch(cleanPreviousAnswers())
    }
    dispatch(setTestId(testId))
    dispatch(setIsBetesaTest(testType === 'Betesa Premium'))
    dispatch(setHas360Test(testType === 'Prueba360'))
    dispatch(setUserTestId(userTestId))
    navigate('/test/start')
  }

  useEffect(() => {
    if (dataHome) {
      setItemsThatMatch(
        dataHome
          .map((item) => {
            const typesNotFinished = item.type_category.filter((typeFinishFilter) => !typeFinishFilter.isFinished)
            return { ...item, type_category: typesNotFinished }
          })
          .filter(
            (item) =>
              item.type_category.length > 0 ||
              (Boolean(item.company_questionnaire_id) &&
                item.questionnaire_status === 'published' &&
                item.questionnaire_submission_status !== 'submitted')
          )
      )
    }
  }, [dataHome])

  useEffect(() => {
    const getCardTextStartButton = async () => {
      if (itemsThatMatch.length === 0) return

      setLoading(true)
      try {
        for (const item of itemsThatMatch) {
          for (const typeInfo of item.type_category) {
            if (typeInfo.category_test_name === 'Betesa Premium') {
              const response = await fetchUserConsented(String(typeInfo.user_test_id), true)
              const newBetesaResultId = response.data?.betesaResult?.id ?? 0

              if (newBetesaResultId !== currentBetesaResultId) {
                setCurrentBetesaResultId(newBetesaResultId)
                dispatch(setBetesaResultId(newBetesaResultId))
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user consented data:', error)
      } finally {
        setLoading(false)
      }
    }

    if (itemsThatMatch.length > 0) {
      getCardTextStartButton()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentBetesaResultId])

  const handleDisabledClick = () => {
    getWarning(t('global.notifications.testDisabled'))
  }

  const getQuestionnairePendingMessage = (questionnaireName?: string | null) => {
    const questionnaireLabel = questionnaireName?.trim()

    return questionnaireLabel
      ? `Responde primero el cuestionario para activar las pruebas: ${questionnaireLabel}.`
      : 'Responde primero el cuestionario para activar las pruebas.'
  }

  const handleDisabledByQuestionnaire = (questionnaireName?: string | null) => {
    getWarning(getQuestionnairePendingMessage(questionnaireName))
  }

  const hasPendingQuestionnaire = (item: GetDataHome) =>
    Boolean(
      item.company_questionnaire_id &&
        item.questionnaire_status === 'published' &&
        item.questionnaire_submission_status !== 'submitted'
    )

  return (
    <MainLayout style={{ maxWidth: '100%' }}>
      {itemsThatMatch.length > 0 ? (
        <ContainerTest isSingleCard={itemsThatMatch.length === 1}>
          <ContainerCards isSingleCard={itemsThatMatch.length === 1}>
            {itemsThatMatch.map((item, index) => (
              <CardTest key={`${item.company_name}-card-${index}`}>
                {hasPendingQuestionnaire(item) ? (
                  <ProcessHint>{getQuestionnairePendingMessage(item.questionnaire_name)}</ProcessHint>
                ) : null}
                <TitleCard>
                  {item.process_name} - {item.company_name}
                </TitleCard>
                <CardTestItemContentParagraph>{t('global.common.pendingTestsText')}</CardTestItemContentParagraph>
                {item.company_questionnaire_id && item.questionnaire_status === 'published' ? (
                  <CardTestItem key={`questionnaire-${item.company_questionnaire_id}`}>
                    <>
                      <CardTestItemImgDiv>
                        <FileFolrderIcon />
                      </CardTestItemImgDiv>
                      <CardTestItemContentDiv>
                        <CardTestItemContentTitle>{item.questionnaire_name || 'Cuestionario'}</CardTestItemContentTitle>
                        <CardTestItemContentStartDiv>
                          <CardTestItemContentDurationDiv>
                            <ClockIcon />
                            <CardTestItemContentDescription>10 min</CardTestItemContentDescription>
                          </CardTestItemContentDurationDiv>
                          <ButtonContainer>
                            <StrokedButton
                              onClick={() =>
                                handleStartTest(
                                  {
                                    companyName: item.process_name,
                                    testLanguage: 'ES',
                                    testType: 'Cuestionario',
                                    companyId: item.company_id,
                                    processId: item.process_id,
                                    questionnaireId: item.company_questionnaire_id || undefined,
                                    processQuestionnaireId: item.process_questionnaire_id || undefined,
                                    questionnaireName: item.questionnaire_name || undefined,
                                  },
                                  0,
                                  0
                                )
                              }
                              type="button"
                            >
                              Iniciar
                            </StrokedButton>
                          </ButtonContainer>
                        </CardTestItemContentStartDiv>
                      </CardTestItemContentDiv>
                    </>
                  </CardTestItem>
                ) : null}
                {item?.type_category.map((typeTestInfo, indexTest) =>
                  typeTestInfo.isFinished || typeTestInfo.isInterpreted || indexTest > 1 ? null : (
                    <CardTestItem key={`${typeTestInfo.test_id}-typeTestInfo-of-test-${indexTest}`}>
                      <>
                        <CardTestItemImgDiv>
                          {typeTestInfo.category_test_name === 'Betesa Premium' ? (
                            <TestItemImg src={talentsImg} alt="Talentos naturales - " />
                          ) : typeTestInfo.category_test_name === 'Prueba360' ? (
                            <TestItemImg src={test360Img} alt="Test 360°" />
                          ) : (
                            <TestItemImg src={skillsImg} alt="Competencias " />
                          )}
                        </CardTestItemImgDiv>
                        <CardTestItemContentDiv>
                          {typeTestInfo.category_test_name === 'Betesa Premium' ? (
                            <CardTestItemContentTitle>
                              {typeTestInfo.is_follow_up ? 'Betesa - Follow Up' : 'Betesa'}
                            </CardTestItemContentTitle>
                          ) : typeTestInfo.category_test_name === 'Prueba360' ? (
                            <CardTestItemContentTitle>{t('global.common.threeSixtyText')}</CardTestItemContentTitle>
                          ) : (
                            <CardTestItemContentTitle>
                              {t('global.common.skillText')}
                              {typeTestInfo.is_follow_up ? ' · Follow Up' : ''}
                            </CardTestItemContentTitle>
                          )}
                          <CardTestItemContentStartDiv>
                            {typeTestInfo.category_test_name === 'Betesa Premium' ? (
                              <CardTestItemContentDurationDiv>
                                <ClockIcon />
                                <CardTestItemContentDescription>25 min</CardTestItemContentDescription>
                              </CardTestItemContentDurationDiv>
                            ) : typeTestInfo.category_test_name === 'Prueba360' ? (
                              <CardTestItemContentDurationDiv>
                                <ClockIcon />
                                <CardTestItemContentDescription>25 min</CardTestItemContentDescription>
                              </CardTestItemContentDurationDiv>
                            ) : (
                              <CardTestItemContentDurationDiv>
                                <ClockIcon />
                                <CardTestItemContentDescription>25 min</CardTestItemContentDescription>
                              </CardTestItemContentDurationDiv>
                            )}
                            <ButtonContainer>
                              <StrokedButton
                                onClick={() =>
                                  handleStartTest(
                                    {
                                      companyName: item.company_name,
                                      testLanguage: 'ES',
                                      testType: typeTestInfo.category_test_name,
                                      companyId: item.company_id,
                                      processId: item.process_id,
                                    },
                                    typeTestInfo.test_id,
                                    typeTestInfo.user_test_id,
                                    typeTestInfo.answer_device
                                  )
                                }
                                type="submit"
                                disabled={Boolean(typeTestInfo.expired || hasPendingQuestionnaire(item))}
                                onDisabledClick={
                                  hasPendingQuestionnaire(item)
                                    ? () => handleDisabledByQuestionnaire(item.questionnaire_name)
                                    : handleDisabledClick
                                }
                              >
                                {typeTestInfo.informed_consent
                                  ? t('global.common.resumeTestButtonText')
                                  : t('global.common.startTestButtonText')}
                              </StrokedButton>
                            </ButtonContainer>
                          </CardTestItemContentStartDiv>
                        </CardTestItemContentDiv>
                      </>
                    </CardTestItem>
                  )
                )}
              </CardTest>
            ))}
          </ContainerCards>
        </ContainerTest>
      ) : (
        <NotTest>
          <p>{t('evaluatedUser.noTestEmptyState')}</p>
        </NotTest>
      )}
      <ModalLoader isLoading={loading} />
    </MainLayout>
  )
}

export default Home
